import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, Envelope, Play, Pause, Users, CircleNotch,
  PaperPlaneTilt, CheckCircle, Clock, Trash, Plus,
  UserPlus, X, MagnifyingGlass, Warning, ArrowsClockwise,
  ListNumbers
} from '@phosphor-icons/react';

const ENROLLMENT_STATUS = {
  active: { label: 'Activo', color: 'bg-green-100 text-green-700' },
  paused: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completado', color: 'bg-blue-100 text-blue-700' },
  pending: { label: 'Pendiente', color: 'bg-slate-100 text-slate-700' },
};

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnroll, setShowEnroll] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/campaigns/${id}`, { withCredentials: true });
      setCampaign(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  const fetchProspects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/prospects`, { withCredentials: true });
      // Filter out already enrolled
      const enrolledIds = (campaign?.enrollments || []).map(e => e.prospect_id);
      setProspects(res.data.filter(p => !enrolledIds.includes(p.id) && p.contact_email));
    } catch (err) {
      console.error(err);
    }
  };

  const handleEnroll = async () => {
    if (selectedProspects.length === 0) return;
    setEnrolling(true);
    try {
      await axios.post(`${API_URL}/api/b2b/campaigns/${id}/enroll`, { prospect_ids: selectedProspects }, { withCredentials: true });
      setShowEnroll(false);
      setSelectedProspects([]);
      fetchCampaign();
    } catch (err) {
      alert('Error inscribiendo prospectos');
    } finally {
      setEnrolling(false);
    }
  };

  const handleUnenroll = async (prospectId) => {
    if (!window.confirm('Quitar este prospecto de la campana?')) return;
    try {
      await axios.post(`${API_URL}/api/b2b/campaigns/${id}/unenroll`, { prospect_id: prospectId }, { withCredentials: true });
      fetchCampaign();
    } catch (err) {
      alert('Error');
    }
  };

  const handleToggleEnrollment = async (enrollmentId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'pause' : 'resume';
      await axios.post(`${API_URL}/api/b2b/campaigns/enrollments/${enrollmentId}/${action}`, {}, { withCredentials: true });
      fetchCampaign();
    } catch (err) {
      alert('Error');
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/campaigns/${id}/activate`, {}, { withCredentials: true });
      fetchCampaign();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error activando');
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API_URL}/api/b2b/campaigns/${id}/pause`, {}, { withCredentials: true });
      fetchCampaign();
    } catch (err) {
      alert('Error pausando');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <Warning size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Campana no encontrada</p>
      </div>
    );
  }

  const enrollments = campaign.enrollments || [];
  const steps = campaign.steps || [];

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/campaigns')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-campaigns">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                campaign.status === 'active' ? 'bg-green-100 text-green-700' :
                campaign.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {campaign.status === 'active' ? 'Activa' : campaign.status === 'paused' ? 'Pausada' : 'Borrador'}
              </span>
            </div>
            <p className="text-sm text-slate-500">{campaign.description || 'Sin descripcion'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {campaign.status === 'active' ? (
            <button onClick={handlePause} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600" data-testid="pause-campaign-btn">
              <Pause size={18} /> Pausar
            </button>
          ) : (
            <button onClick={handleActivate} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="activate-campaign-btn">
              <Play size={18} /> Activar
            </button>
          )}
          <button
            onClick={() => { setShowEnroll(true); fetchProspects(); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="enroll-prospects-btn"
          >
            <UserPlus size={18} /> Inscribir Prospectos
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Pasos</p>
          <p className="text-2xl font-bold text-slate-900">{steps.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Inscritos</p>
          <p className="text-2xl font-bold text-violet-600">{campaign.enrolled_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Activos</p>
          <p className="text-2xl font-bold text-green-600">{campaign.active_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Emails Enviados</p>
          <p className="text-2xl font-bold text-amber-600">{campaign.stats?.total_sent || 0}</p>
        </div>
      </div>

      {/* Steps Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ListNumbers size={20} /> Secuencia de Emails
        </h2>
        {steps.length === 0 ? (
          <p className="text-slate-500 text-sm">No hay pasos definidos. Edita la campana para agregar pasos.</p>
        ) : (
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </div>
                  {idx < steps.length - 1 && <div className="w-0.5 h-8 bg-blue-200 mt-1" />}
                </div>
                <div className="flex-1 pb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={14} className="text-slate-400" />
                    <span className="text-slate-500">
                      {idx === 0 ? `Enviar al inscribir${step.delay_days > 0 ? ` + ${step.delay_days} dias` : ''}` : `Esperar ${step.delay_days} dias`}
                    </span>
                  </div>
                  <p className="font-medium text-slate-800 mt-1">{step.subject || '(Sin asunto)'}</p>
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{step.body?.substring(0, 120)}...</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enrollments */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Users size={20} /> Prospectos Inscritos ({enrollments.length})
        </h2>

        {enrollments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Users size={36} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No hay prospectos inscritos en esta campana</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                  <th className="text-left p-3 font-medium text-slate-600">Contacto</th>
                  <th className="text-left p-3 font-medium text-slate-600">Email</th>
                  <th className="text-center p-3 font-medium text-slate-600">Paso</th>
                  <th className="text-center p-3 font-medium text-slate-600">Estado</th>
                  <th className="text-center p-3 font-medium text-slate-600">Proximo Envio</th>
                  <th className="text-center p-3 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enrollments.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{e.prospect_name}</td>
                    <td className="p-3 text-slate-600">{e.contact_name}</td>
                    <td className="p-3 text-slate-600">{e.contact_email}</td>
                    <td className="p-3 text-center">
                      <span className="text-sm font-medium text-blue-600">
                        {e.current_step + 1} / {steps.length}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ENROLLMENT_STATUS[e.status]?.color || 'bg-slate-100'}`}>
                        {ENROLLMENT_STATUS[e.status]?.label || e.status}
                      </span>
                    </td>
                    <td className="p-3 text-center text-xs text-slate-500">
                      {e.status === 'completed' ? (
                        <span className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle size={14} /> Completado
                        </span>
                      ) : e.next_send_at ? (
                        new Date(e.next_send_at).toLocaleDateString('es', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      ) : '-'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {e.status !== 'completed' && (
                          <button
                            onClick={() => handleToggleEnrollment(e.id, e.status)}
                            className={`p-1.5 rounded ${e.status === 'active' ? 'text-amber-500 hover:bg-amber-50' : 'text-green-500 hover:bg-green-50'}`}
                            title={e.status === 'active' ? 'Pausar' : 'Reanudar'}
                          >
                            {e.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                          </button>
                        )}
                        <button
                          onClick={() => handleUnenroll(e.prospect_id)}
                          className="p-1.5 rounded text-red-400 hover:bg-red-50"
                          title="Quitar"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enroll Modal */}
      {showEnroll && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" data-testid="enroll-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-slate-900">Inscribir Prospectos</h3>
              <button onClick={() => { setShowEnroll(false); setSelectedProspects([]); }} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <div className="relative mb-4">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-lg"
                  placeholder="Buscar prospectos..."
                  data-testid="enroll-search-input"
                />
              </div>
              <p className="text-xs text-slate-500 mb-2">Solo se muestran prospectos con email que no estan inscritos</p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {prospects
                  .filter(p => !searchTerm || p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProspects.includes(p.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedProspects([...selectedProspects, p.id]);
                          else setSelectedProspects(selectedProspects.filter(id => id !== p.id));
                        }}
                        className="rounded border-slate-300"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{p.company_name}</p>
                        <p className="text-xs text-slate-500">{p.contact_name} - {p.contact_email}</p>
                      </div>
                    </label>
                  ))}
                {prospects.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No hay prospectos disponibles para inscribir</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between p-5 border-t">
              <span className="text-sm text-slate-500">{selectedProspects.length} seleccionados</span>
              <button
                onClick={handleEnroll}
                disabled={enrolling || selectedProspects.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-enroll-btn"
              >
                {enrolling && <CircleNotch size={16} className="animate-spin" />}
                Inscribir ({selectedProspects.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
